import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery, useGetZohoSalesIQSetupMutation } from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { numberScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import { EditZohoSalesIQAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoSalesIQIntegrationSettings/EditZohoSalesIQAutoSetupSettings/EditZohoSalesIQAutoSetupSettings';
import { EditAutocloseAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditAutocloseAdvancedSettings/EditAutocloseAdvancedSettings';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

interface ZohoSalesIQSettingsFormValues extends AutocloseFormValues {
  project_name: string;
}

export const EditZohoSalesIQIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);
  const [code, setCode] = React.useState('');
  const [getZohoSalesIQSetup, { data: setupResponse, isLoading: isLoadingZohoSalesIQSetup }] =
    useGetZohoSalesIQSetupMutation();

  useEffect(() => {
    if (id && integration.id) {
      getZohoSalesIQSetup({
        projectId: id,
        integrationId: integration.id,
      });
    }
  }, [id, integration]);

  useEffect(() => {
    if (setupResponse?.plug_code) {
      setCode(setupResponse.plug_code);
    }
  }, [setupResponse]);

  useLoaderWhile(isLoadingUpdateProject || isLoadingZohoSalesIQSetup);

  const methods = useForm<ZohoSalesIQSettingsFormValues>({
    defaultValues: {
      project_name: integration?.integration_data?.name || '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
    },
    resolver: formResolver(
      object({
        name: stringScheme({ required: false }).label('Project Name'),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
      }),
    ),
  });

  const submit = async (data: ZohoSalesIQSettingsFormValues) => {
    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="auto-setup-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Setup settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon
                    open={accordionValue.includes('web-widget-settings')}
                  />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoSalesIQAutoSetupSettings code={code} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditAutocloseAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
